<script setup>
import { onBeforeUnmount, ref, watch } from "vue"
import { Editor, EditorContent } from "@tiptap/vue-3"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Image from "@tiptap/extension-image"
import Underline from "@tiptap/extension-underline"
import Placeholder from "@tiptap/extension-placeholder"
import { DirectUpload } from "@rails/activestorage"
import { post } from "../api"

const props = defineProps({ modelValue: { type: String, default: "" }, placeholder: { type: String, default: "Добавьте описание…" } })
const emit = defineEmits(["update:modelValue"])
const uploading = ref(0), lightbox = ref([]), lightboxIndex = ref(0)
const editor = new Editor({
  content: props.modelValue,
  extensions: [ StarterKit.configure({ heading: { levels: [ 1, 2, 3, 4, 5, 6 ] } }), Underline, Link.configure({ openOnClick: false }), Image, Placeholder.configure({ placeholder: props.placeholder }) ],
  editorProps: {
    handleDrop(_view, event) {
      const files = Array.from(event.dataTransfer?.files || [])
      if (!files.length) return false
      files.forEach(uploadFile)
      return true
    },
    handlePaste(_view, event) {
      const images = Array.from(event.clipboardData?.files || []).filter(file => file.type.startsWith("image/"))
      if (!images.length) return false
      images.forEach(uploadFile)
      return true
    }
  },
  onUpdate: ({ editor: instance }) => emit("update:modelValue", instance.getHTML())
})

watch(() => props.modelValue, value => {
  if (editor.getHTML() !== value) editor.commands.setContent(value || "", false)
})
function keyboard(event) {
  if (!lightbox.value.length) return
  if (event.key === "Escape") lightbox.value = []
  if (event.key === "ArrowRight") lightboxIndex.value = (lightboxIndex.value + 1) % lightbox.value.length
  if (event.key === "ArrowLeft") lightboxIndex.value = (lightboxIndex.value - 1 + lightbox.value.length) % lightbox.value.length
}
window.addEventListener("keydown", keyboard)
onBeforeUnmount(() => { window.removeEventListener("keydown", keyboard); editor.destroy() })

function setLink() {
  const current = editor.getAttributes("link").href
  const href = window.prompt("URL", current || "https://")
  if (href === null) return
  href ? editor.chain().focus().extendMarkRange("link").setLink({ href }).run() : editor.chain().focus().unsetLink().run()
}
function chooseFiles() {
  const input = document.createElement("input")
  input.type = "file"; input.multiple = true
  input.onchange = () => Array.from(input.files || []).forEach(uploadFile)
  input.click()
}
function uploadFile(file) {
  if (file.size > 10 * 1024 * 1024) return window.alert(`${file.name}: максимальный размер файла — 10 МБ`)
  uploading.value += 1
  new DirectUpload(file, "/rails/active_storage/direct_uploads").create(async (error, blob) => {
    if (error) { uploading.value -= 1; return window.alert(`Не удалось загрузить ${file.name}: ${error}`) }
    try {
      const stored = await post("/uploaded_files", { signed_id: blob.signed_id })
      if (file.type.startsWith("image/")) editor.chain().focus().setImage({ src: stored.url, alt: file.name, title: file.name }).run()
      else editor.chain().focus().insertContent({ type: "text", text: file.name, marks: [ { type: "link", attrs: { href: stored.url, target: "_blank" } } ] }).run()
    } catch (exception) { window.alert(`Не удалось сохранить ${file.name}: ${exception.message}`) }
    finally { uploading.value -= 1 }
  })
}
function openImage(event) {
  if (event.target.tagName !== "IMG") return
  const images = Array.from(event.currentTarget.querySelectorAll(".ProseMirror img"))
  lightbox.value = images.map(image => ({ src: image.src, alt: image.alt || image.title || "Изображение" }))
  lightboxIndex.value = images.indexOf(event.target)
}
</script>

<template><div class="tiptap-editor" @dragover.prevent><div class="editor-toolbar">
  <button v-for="level in [1,2,3,4,5,6]" :key="level" type="button" :class="{active:editor.isActive('heading',{level})}" @click="editor.chain().focus().toggleHeading({level}).run()">H{{ level }}</button>
  <span></span><button type="button" :class="{active:editor.isActive('bold')}" @click="editor.chain().focus().toggleBold().run()"><b>B</b></button><button type="button" :class="{active:editor.isActive('italic')}" @click="editor.chain().focus().toggleItalic().run()"><i>I</i></button><button type="button" :class="{active:editor.isActive('underline')}" @click="editor.chain().focus().toggleUnderline().run()"><u>U</u></button><button type="button" :class="{active:editor.isActive('strike')}" @click="editor.chain().focus().toggleStrike().run()"><s>S</s></button>
  <span></span><button type="button" @click="editor.chain().focus().toggleBulletList().run()">• List</button><button type="button" @click="editor.chain().focus().toggleOrderedList().run()">1. List</button><button type="button" @click="editor.chain().focus().toggleBlockquote().run()">❝</button><button type="button" @click="editor.chain().focus().toggleCodeBlock().run()">&lt;/&gt;</button><button type="button" @click="setLink">🔗</button><button type="button" @click="chooseFiles">📎</button>
</div><div class="editor-content-wrap" @click="openImage"><EditorContent :editor="editor"/><span v-if="uploading" class="editor-uploading">Загрузка файлов: {{ uploading }}</span></div>
  <div v-if="lightbox.length" class="image-lightbox" @click.self="lightbox=[]"><button class="lightbox-close" type="button" @click="lightbox=[]">×</button><button v-if="lightbox.length>1" class="lightbox-prev" type="button" @click="lightboxIndex=(lightboxIndex-1+lightbox.length)%lightbox.length">‹</button><figure><img :src="lightbox[lightboxIndex].src" :alt="lightbox[lightboxIndex].alt"><figcaption>{{ lightbox[lightboxIndex].alt }} · {{ lightboxIndex + 1 }}/{{ lightbox.length }} <a :href="lightbox[lightboxIndex].src" target="_blank">Открыть оригинал</a></figcaption></figure><button v-if="lightbox.length>1" class="lightbox-next" type="button" @click="lightboxIndex=(lightboxIndex+1)%lightbox.length">›</button></div>
</div></template>
